'use client'

/**
 * Exit Intent Hook
 *
 * Detects user exit intent via multiple signals, all opt-in per caller:
 * - Desktop: mouse leaving the viewport toward browser chrome
 * - Scroll depth (tool mode fires at the threshold; content mode arms at
 *   the threshold and fires on scroll-up, idle, or read-complete)
 * - Timed trigger when `timeThreshold` is set
 *
 * Fires once per mount and reports WHICH signal fired (`triggerSignal`) so
 * the impression event can be split by signal.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { inScrollLockGrace } from '@/lib/scroll-lock-grace'

/** Which signal opened the trigger. Rides on impression events as `trigger_signal`. */
export type ExitIntentSignal =
  | 'mouseleave'
  | 'timer'
  | 'scroll_depth'
  | 'scroll_up'
  | 'idle'
  | 'read_complete'

interface UseExitIntentOptions {
  /** Whether the hook should be active */
  enabled: boolean
  /** Scroll depth threshold (0-1), default 0.9 (90%) */
  scrollThreshold?: number
  /** Time threshold in ms. Pass `undefined` (or omit) to disable the
   *  time-based fallback entirely — only mouseleave + scroll signals fire. */
  timeThreshold?: number
  /** Delay before exit intent can trigger (ms), default 5000 (5 seconds) */
  initialDelay?: number
  /**
   * Content-page mode for the scroll signal. On articles, raw scroll depth is
   * an ENGAGEMENT signal, not an exit signal — firing at 60% depth interrupts
   * a mid-read reader (the blog modal's day-one data: ~70% explicit
   * dismissals). When true, reaching `scrollThreshold` only ARMS the trigger;
   * it fires when the reader then scrolls back up (a real leave/re-orient
   * gesture) or reaches the end of the page (95%+, read complete). Tool pages
   * keep the default depth-fires-immediately behavior.
   */
  requireScrollUpAfterDepth?: boolean
  /**
   * Content-page mode only. Once armed, a reader who stops scrolling for this
   * many ms counts as paused (finished a section, reading the ad, deciding
   * what to do next) and the trigger fires. Each scroll while armed resets the
   * timer, so a reader still moving down the page is never interrupted.
   * Omit to disable.
   */
  idleAfterArmMs?: number
}

interface UseExitIntentReturn {
  /** Whether exit intent has been triggered */
  hasTriggered: boolean
  /** The signal that fired, null until hasTriggered. */
  triggerSignal: ExitIntentSignal | null
  /** Manually reset the trigger (for testing) */
  reset: () => void
}

/** Scroll-up distance (px) that counts as a leave/re-orient gesture once armed. */
const SCROLL_UP_TRIGGER_PX = 120
/** Depth at which the page counts as read-complete regardless of scroll-up. */
const READ_COMPLETE_DEPTH = 0.95

export function useExitIntent({
  enabled,
  scrollThreshold = 0.9,
  timeThreshold,
  initialDelay = 5000,
  requireScrollUpAfterDepth = false,
  idleAfterArmMs
}: UseExitIntentOptions): UseExitIntentReturn {
  const [hasTriggered, setHasTriggered] = useState(false)
  const [triggerSignal, setTriggerSignal] = useState<ExitIntentSignal | null>(null)
  const hasTriggeredRef = useRef(false)
  const canTriggerRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Trigger function (only fires once)
  const trigger = useCallback((signal: ExitIntentSignal) => {
    if (hasTriggeredRef.current || !canTriggerRef.current) return
    hasTriggeredRef.current = true
    setTriggerSignal(signal)
    setHasTriggered(true)
  }, [])

  // Reset function (for testing)
  const reset = useCallback(() => {
    hasTriggeredRef.current = false
    setTriggerSignal(null)
    setHasTriggered(false)
  }, [])

  useEffect(() => {
    if (!enabled) return

    // Delay before enabling (prevents immediate trigger)
    delayTimeoutRef.current = setTimeout(() => {
      canTriggerRef.current = true
    }, initialDelay)

    // --- Desktop: Mouse leave detection ---
    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse leaves toward top of viewport (browser chrome)
      if (e.clientY <= 0 && canTriggerRef.current) {
        trigger('mouseleave')
      }
    }

    // --- Mobile: Scroll depth detection ---
    // Content-page mode state: armed once threshold depth is reached; the
    // deepest scrollY seen while armed anchors the scroll-up measurement.
    let armed = false
    let maxScrollYWhileArmed = 0

    const handleScroll = () => {
      if (!canTriggerRef.current) return

      // A fullscreen layer that locks the body (position:fixed — the map's
      // fullscreen drawing mode, the chat widget) collapses scrollHeight and
      // pins scrollY at 0, which can read as ≥ threshold depth on short
      // documents. Depth measured against a locked body is meaningless, so
      // never treat it as an exit signal. The grace check covers the lock's
      // OWN release: its restoration scrollTo fires after the styles clear.
      if (document.body.style.position === 'fixed' || inScrollLockGrace()) return

      const scrollHeight = document.documentElement.scrollHeight
      const scrollTop = window.scrollY
      const clientHeight = window.innerHeight
      const scrollDepth = (scrollTop + clientHeight) / scrollHeight

      if (!requireScrollUpAfterDepth) {
        if (scrollDepth >= scrollThreshold) {
          trigger('scroll_depth')
        }
        return
      }

      // Content-page mode: depth alone never fires mid-read.
      if (scrollDepth >= READ_COMPLETE_DEPTH) {
        // Reader reached the end — read complete counts as a natural pause.
        trigger('read_complete')
        return
      }

      if (scrollDepth >= scrollThreshold) {
        armed = true
      }
      if (!armed) return

      if (typeof idleAfterArmMs === 'number') {
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current)
        idleTimeoutRef.current = setTimeout(() => trigger('idle'), idleAfterArmMs)
      }

      if (scrollTop > maxScrollYWhileArmed) {
        maxScrollYWhileArmed = scrollTop
      } else if (maxScrollYWhileArmed - scrollTop >= SCROLL_UP_TRIGGER_PX) {
        // Armed reader scrolled back up — leave/re-orient gesture.
        trigger('scroll_up')
      }
    }

    // --- Time threshold (opt-in) ---
    // Time-based fallback was the worst-converting and most-annoying signal
    // (engaged readers got popup-bombed mid-thought). Now opt-in only — caller
    // must pass an explicit number to enable.
    if (typeof timeThreshold === 'number') {
      timeoutRef.current = setTimeout(() => {
        if (canTriggerRef.current) {
          trigger('timer')
        }
      }, timeThreshold)
    }

    // Attach listeners
    document.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave)
      window.removeEventListener('scroll', handleScroll)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current)
    }
  }, [enabled, scrollThreshold, timeThreshold, initialDelay, requireScrollUpAfterDepth, idleAfterArmMs, trigger])

  return { hasTriggered, triggerSignal, reset }
}
